import { useLocalStorageValue } from '@react-hookz/web'
import Cookies from 'js-cookie'
import { z } from 'zod'

/**
 * The single source of truth for the shape of the `user-settings` blob held in
 * localStorage and mirrored to a cookie for SSR.
 *
 * Exported because its backwards compatibility is a real correctness property,
 * not an implementation detail: `mergeUserSettings` below runs `.parse` on every
 * write, so any field added here without a default would throw for every user
 * whose stored blob predates it — silently breaking *all* preference writes,
 * not just the new one. Keeping the schema reachable lets that guarantee be
 * asserted directly against persisted payloads rather than inferred by reading
 * the field list.
 */
export const settingsSchema = z.object({
  sidebarWidth: z.number().default(280),
  inboxListWidth: z.number().default(280),
  contactsView: z.enum(['list', 'board']).default('board'),
  contactsColumns: z
    .array(z.string())
    .default(['name', 'email', 'createdAt', 'type', 'status']),
  contactsGroupBy: z.string().default('status'),
  lastSelectedRepository: z.record(z.string(), z.string()).default({}),
  // The model id the user last *started* a task or session with. A flat string
  // rather than a per-workspace record like `lastSelectedRepository`, because
  // the model catalog comes from platform-level config and is identical in every
  // workspace — a model preference should follow the user, not the workspace.
  //
  // `null` means "nothing remembered yet", which is distinct from a remembered
  // id that no longer exists; only the former should silently take the platform
  // default. `.default(null)` is what keeps this backwards compatible:
  // `setUserSettings` runs `settingsSchema.parse` on every write, so a required
  // field with no default would throw on the first write for every existing user
  // whose stored blob predates this key.
  lastSelectedModel: z.string().nullable().default(null),
  branchActionPrimary: z.enum(['copy', 'cursor']).default('copy'),
})

type UserSettings = z.infer<typeof settingsSchema>

const defaultSettings: UserSettings = {
  sidebarWidth: 280,
  inboxListWidth: 280,
  contactsView: 'list',
  contactsColumns: ['name', 'email', 'createdAt', 'type', 'status'],
  contactsGroupBy: 'status',
  lastSelectedRepository: {},
  lastSelectedModel: null,
  branchActionPrimary: 'copy',
}

type SetUserSettings = <Key extends keyof UserSettings>(
  key: keyof UserSettings,
  value: UserSettings[Key],
) => void

/**
 * The blob to persist when writing `key`, merged onto a *validated* base rather
 * than onto the raw stored value.
 *
 * `readUserSettings` already treats an unparseable blob as "fall back to
 * defaults", so a user whose storage is corrupt — hand-edited, rewritten by an
 * extension, or written by a build whose schema disagrees — sees a perfectly
 * normal app running on default preferences. Spreading that same raw blob into
 * a throwing `parse` made the write path disagree with the read path: the first
 * preference write of the session would throw.
 *
 * That asymmetry only became user-facing once the start flows began recording
 * `lastSelectedModel` *after* a successful start, from inside their `try`. The
 * throw landed in the `catch` that reports "Failed to start …", so a task that
 * had genuinely been created was announced as a failure and the navigation to it
 * never ran — leaving the user on the composer, primed to start a duplicate.
 * Merging onto the same value the read path would have handed the UI keeps a
 * corrupt blob what it already looked like (preferences silently reset) rather
 * than turning it into a phantom start failure.
 *
 * The merged result is still `parse`d, so an invalid *new* value — a caller bug
 * rather than bad stored data — keeps throwing loudly.
 */
export function mergeUserSettings<Key extends keyof UserSettings>(
  stored: unknown,
  key: Key,
  value: UserSettings[Key],
): UserSettings {
  return settingsSchema.parse({ ...readUserSettings(stored), [key]: value })
}

/**
 * The settings to render for a stored blob, falling back to defaults when it
 * cannot be parsed. Shared with `mergeUserSettings` so that what is written back
 * is always the same thing the user was just looking at.
 */
export function readUserSettings(stored: unknown): UserSettings {
  const parsed = settingsSchema.safeParse(stored)
  return parsed.success ? parsed.data : defaultSettings
}

export const useUserSettings = () => {
  const defaultValue = defaultSettings

  const { value, set } = useLocalStorageValue<UserSettings>('user-settings', {
    defaultValue,
    stringify(data) {
      const settings = JSON.stringify(data)
      Cookies.set('user-settings', settings, {
        expires: 365,
        path: '/',
        sameSite: 'lax',
      })
      return settings
    },
  })

  const setUserSettings: SetUserSettings = (key, value) => {
    set((prev) => mergeUserSettings(prev, key, value))
  }

  const settings = readUserSettings(value ?? defaultValue)

  return [settings, setUserSettings] as const
}
